import { useTranslations } from '@vocab/react';
import {
  Box,
  IconCaution,
  PageBlock,
  Strong,
  Text,
  TextLinkButton,
  useToast,
} from 'braid-design-system';
import { useEffect, type ReactNode } from 'react';

import { trackPageInteraction } from 'src/modules/AnalyticsTracker';
import { useResendVerificationEmail } from 'src/modules/ApiClient';

import translations from './.vocab';
interface Props {
  email?: string;
}

export const VerifyEmailAlert = ({ email }: Props) => {
  const showToast = useToast();
  const { mutate: resendVerificationEmail } = useResendVerificationEmail();
  const { t } = useTranslations(translations);

  useEffect(() => {
    if (email) {
      const message = `Click on the confirmation link sent to [REDACTED]`;
      trackPageInteraction('alert_viewed', undefined, {
        alertShown: {
          type: 'banner',
          tone: 'cautionary',
          position: 'header',
          title: 'Verify your Email to complete registration',
          message,
        },
      });
    }
  }, [email]);

  const fireResendEmailRequest = async () => {
    trackPageInteraction(
      'content_element_pressed',
      'email verification banner',
      {
        elementDesign: 'link',
        elementText: 'Resend the confirmation email',
        elementAction: 'resend confirmation email',
      },
    );

    try {
      const response = await resendVerificationEmail();
      const success = response.data?.sendEmailVerificationEmail?.success;

      return showToast({
        tone: success ? 'positive' : 'critical',
        message: success
          ? t('Resent Verification Email Sent')
          : t('Resent Verification Email Failure'),
      });
    } catch {
      return showToast({
        tone: 'critical',
        message: t('Resent Verification Email Failure'),
      });
    }
  };

  return (
    <Box paddingY="medium" background="cautionLight">
      <PageBlock width="medium">
        <Text icon={<IconCaution />}>
          {t('Click to confirm email', {
            Strong: (children: ReactNode[]) => (
              <Strong>{email ?? children}</Strong>
            ),
          })}{' '}
          {t(`Can't find the email?`)}{' '}
          {t('Resend or contact cs', {
            Link: (children: any) => (
              <TextLinkButton onClick={fireResendEmailRequest}>
                {children}
              </TextLinkButton>
            ),
          })}
        </Text>
      </PageBlock>
    </Box>
  );
};
